'use client';

/**
 * Welcome splash — the "app wall" landing (variant A, approved
 * 2026-06-12). Single-screen, white surface, styled to match the
 * "Welcome to Robutler" modal + the workspace chrome so it reads as
 * native Robutler:
 *   • background aura        → the EXACT live-landing `GuestLandingGradient`
 *   • glass logo pill        → the workspace brand mark (no menu here)
 *   • buttons                → `.btn-primary`
 *   • headings               → `.font-display`
 *   • app tiles              → video-thumbnail cards (`.card`-clean)
 *   • sign-in                → canonical `/login` (LoginDialogBody)
 *
 * Content is flexed out vertically (`justify-evenly`) between a header
 * (top) and footer (bottom). One viewport on `sm+`; mobile flows and
 * scrolls.
 *
 * Clicking a card FLIP-zooms it to a centred dialog (backdrop + the
 * clip playing large + an "Open app" CTA). The CTA goes to sign-up
 * (sign-in for a returning visitor) with `/?app=<id>` as the return
 * address, so the app opens in the person's own workspace once they have
 * an account (2026-10-12; it used to mint a guest). Card media uses
 * local placeholder clips — the real screen recordings drop into the
 * same `<video>` slot.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  type MouseEvent as ReactMouseEvent,
  type DragEvent as ReactDragEvent,
} from 'react';
import Image from 'next/image';
import { useT } from '@/components/i18n-provider';
import Link from 'next/link';
import {
  ArrowRight,
  Check,
  Plus,
  X,
  Video,
  PenTool,
  FileText,
  Frame,
  Music,
  Cpu,
  Gamepad2,
  Play,
  type LucideIcon,
} from 'lucide-react';
import { GuestLandingGradient } from '@/components/guest-landing-gradient';
import { WelcomeSections } from '@/components/splash/welcome-sections';
import { LiquidGlassDefs } from '@/components/workspace/liquid-glass-defs';
import { useAnalytics } from '@/hooks/use-analytics';
import { COHORTS, UTM_TO_COHORT, COHORT_STORAGE_KEY } from '@/lib/splash/cohorts';

// `join: true` marks the scope/lead tile (its mp4 cycles the app categories) —
// it opens the zoom dialog like the apps, but its dialog CTA is plain sign-up,
// not "Open app" (sign-up that returns to the app). `soon: true` marks a
// not-yet-launched app: the same plain sign-up CTA, with a "Coming soon" treatment.
type AppTile = { id: string; name: string; sub: string; icon: LucideIcon; tint: string; tint2?: string; video?: string; join?: boolean; soon?: boolean };

// Local placeholder clips (generated, served from /public) so the
// layout can be felt with real motion. Swap each for the app's own
// screen recording.
const SAMPLE = ['/samples/s1.mp4', '/samples/s2.mp4', '/samples/s3.mp4', '/samples/s4.mp4', '/samples/s5.mp4', '/samples/s6.mp4', '/samples/s7.mp4', '/samples/s8.mp4'];

// Default ring order. Lead (centered) is Docs, with Video studio as its right
// neighbor and Vector design as its left neighbor (last index wraps to the
// left of index 0). The "Get an app" scope tile sits at the tail, just before
// Vector, so it's a discoverable closer rather than the first thing centered.
// Per-cohort orders are derived from this via `orderAppsForCohort`.
const APPS: AppTile[] = [
  { id: 'docs', name: 'Docs', sub: 'writes and cites with you', icon: FileText, tint: '#38bdf8', tint2: '#34d399', video: SAMPLE[2] },
  { id: 'video-studio', name: 'Video Studio', sub: 'a studio in a tab', icon: Video, tint: '#7c6cf0', tint2: '#ec4899', video: SAMPLE[0] },
  { id: 'guitarro', name: 'Guitarro', sub: 'real tone, in a tab', icon: Music, tint: '#fbbf24', tint2: '#f97316', video: SAMPLE[4] },
  { id: 'whiteboard', name: 'Whiteboard', sub: 'brainstorm at 60fps', icon: Frame, tint: '#22d3ee', tint2: '#6366f1', video: SAMPLE[3] },
  { id: 'more', name: 'Get an App', sub: 'for you and your agents', icon: Plus, tint: '#7f77dd', tint2: '#ec4899', video: SAMPLE[7], join: true },
  { id: 'vector-design', name: 'Vector Design', sub: 'vectors, no seat fee', icon: PenTool, tint: '#f472b6', tint2: '#fb923c', video: SAMPLE[1] },
];

// Coming-soon tiles. NOT in the default deck — they're injected ONLY for a
// cohort whose lead list names them (so they never leak into the default order
// or other cohorts). They carry no clip and use the sign-up CTA + a "Coming
// soon" treatment until launch. Promote one to a real tile by moving it into
// APPS (with a clip) and dropping `soon`.
const COMING_SOON: Record<string, AppTile> = {
  eda: { id: 'eda', name: 'Chip Design', sub: 'schematic to silicon, in a tab', icon: Cpu, tint: '#a3e635', tint2: '#22d3ee', soon: true },
  robongus: { id: 'robongus', name: 'Robongus', sub: 'a playable Turing test', icon: Gamepad2, tint: '#a855f7', tint2: '#ec4899', soon: true },
};

// ── Cohorts ───────────────────────────────────────────────────────────────
// Registry shared with the `/for/<slug>` invite route (lib/splash/cohorts).
// A cohort floats its lead apps to the front; it comes from a /for invite, a
// `?cohort=<key>` param, or a mapped UTM, then persists across visits.

/**
 * Float a cohort's lead apps to the front; rest stay in default order. A lead
 * id resolves to a real tile (APPS) or a cohort-scoped coming-soon tile, so
 * coming-soon cards appear ONLY for the cohort that names them, never in the
 * default deck or other cohorts.
 */
function orderAppsForCohort(cohort: string | null): AppTile[] {
  const lead = cohort ? COHORTS[cohort] : null;
  if (!lead || lead.length === 0) return APPS;
  const byId = new Map(APPS.map((a) => [a.id, a]));
  const leadTiles = lead.map((id) => byId.get(id) ?? COMING_SOON[id]).filter(Boolean) as AppTile[];
  const leadIds = new Set(leadTiles.map((a) => a.id));
  return [...leadTiles, ...APPS.filter((a) => !leadIds.has(a.id))];
}

/**
 * Resolve the active cohort (client-only): explicit `?cohort=` link param wins,
 * then a mapped UTM value, then a previously-persisted choice. On a fresh hit it
 * persists to localStorage + a `rb_cohort` cookie (the cookie lets the server
 * personalize later, e.g. the post-signup landing). Unknown keys are ignored.
 */
function resolveCohort(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const sp = new URLSearchParams(window.location.search);
    const norm = (v: string | null) => (v || '').trim().toLowerCase();

    const explicit = norm(sp.get('cohort'));
    let key: string | null = explicit && COHORTS[explicit] ? explicit : null;

    if (!key) {
      for (const p of ['utm_campaign', 'utm_content', 'utm_source', 'utm_term']) {
        const v = norm(sp.get(p));
        if (!v) continue;
        const mapped = UTM_TO_COHORT[v] || (COHORTS[v] ? v : null);
        if (mapped) { key = mapped; break; }
      }
    }

    if (key) {
      localStorage.setItem(COHORT_STORAGE_KEY, key);
      // 90-day cookie so a later server render can read the same cohort.
      document.cookie = `${COHORT_STORAGE_KEY}=${key}; path=/; max-age=${90 * 86400}; samesite=lax`;
      return key;
    }

    const saved = norm(localStorage.getItem(COHORT_STORAGE_KEY));
    return saved && COHORTS[saved] ? saved : null;
  } catch {
    return null;
  }
}

// Trimmed to the 5 strongest, distinct claims (dropped "remix and publish" —
// already covered by the hero's "open to remix").
const PROMISES = ['natively agentic', 'realtime collab', 'free to use', 'open code', 'you earn'];

// Experimental color schemes for the /welcome exploration. Keys 0–9 switch live
// (longForm only). Each overrides the core `--color-*` tokens (so all existing
// markup recolors) plus the aura intensity (`--hero-gradient-opacity`).
type Scheme = { name: string; dark: boolean; vars: CSSProperties & Record<`--${string}`, string> };
const SCHEMES: Scheme[] = [
  { name: 'Lime noir', dark: true, vars: { '--color-background': '#0a0a0a', '--color-foreground': '#fafafa', '--color-muted-foreground': '#a1a1aa', '--color-border': '#262626', '--color-card': '#141414', '--color-primary': '#cfe04a', '--color-primary-foreground': '#0a0a0a', '--hero-gradient-opacity': '0.12' } },
  { name: 'Electric violet', dark: true, vars: { '--color-background': '#0b0a12', '--color-foreground': '#f4f2f8', '--color-muted-foreground': '#a6a2b8', '--color-border': '#2a2738', '--color-card': '#16141f', '--color-primary': '#8b7bff', '--color-primary-foreground': '#ffffff', '--hero-gradient-opacity': '0.4' } },
  { name: 'Cyber cyan', dark: true, vars: { '--color-background': '#06100f', '--color-foreground': '#e9fdff', '--color-muted-foreground': '#88b5b3', '--color-border': '#143033', '--color-card': '#0e1a1b', '--color-primary': '#22e0d6', '--color-primary-foreground': '#042422', '--hero-gradient-opacity': '0.18' } },
  { name: 'Hot magenta', dark: true, vars: { '--color-background': '#0c0711', '--color-foreground': '#fbeef6', '--color-muted-foreground': '#b89bac', '--color-border': '#2a1830', '--color-card': '#160d19', '--color-primary': '#ff4d9d', '--color-primary-foreground': '#ffffff', '--hero-gradient-opacity': '0.28' } },
  { name: 'Sunset coral', dark: true, vars: { '--color-background': '#100a08', '--color-foreground': '#fff3ea', '--color-muted-foreground': '#c1a594', '--color-border': '#2e2017', '--color-card': '#1a110c', '--color-primary': '#ff7a45', '--color-primary-foreground': '#1a0e08', '--hero-gradient-opacity': '0.3' } },
  { name: 'Midnight blue', dark: true, vars: { '--color-background': '#060a14', '--color-foreground': '#eef3ff', '--color-muted-foreground': '#93a4c4', '--color-border': '#1a2742', '--color-card': '#0d1422', '--color-primary': '#4d8dff', '--color-primary-foreground': '#ffffff', '--hero-gradient-opacity': '0.32' } },
  { name: 'Clean light', dark: false, vars: { '--color-background': '#ffffff', '--color-foreground': '#0f172a', '--color-muted-foreground': '#64748b', '--color-border': '#e2e8f0', '--color-card': '#ffffff', '--color-primary': '#000000', '--color-primary-foreground': '#ffffff', '--hero-gradient-opacity': '0.35' } },
  { name: 'Indigo light', dark: false, vars: { '--color-background': '#fbfbfe', '--color-foreground': '#16142b', '--color-muted-foreground': '#6b6880', '--color-border': '#e6e4f2', '--color-card': '#ffffff', '--color-primary': '#5b4ae0', '--color-primary-foreground': '#ffffff', '--hero-gradient-opacity': '0.45' } },
  { name: 'Warm light', dark: false, vars: { '--color-background': '#fffaf6', '--color-foreground': '#2b1c12', '--color-muted-foreground': '#8a6f5c', '--color-border': '#f0e4d8', '--color-card': '#ffffff', '--color-primary': '#e0552f', '--color-primary-foreground': '#ffffff', '--hero-gradient-opacity': '0.4' } },
  { name: 'Mint light', dark: false, vars: { '--color-background': '#f5fffb', '--color-foreground': '#0c2018', '--color-muted-foreground': '#5e7a6e', '--color-border': '#d8efe4', '--color-card': '#ffffff', '--color-primary': '#00936e', '--color-primary-foreground': '#ffffff', '--hero-gradient-opacity': '0.4' } },
];

// Safety cap (ms) for advancing past a clip that can't reach its `ended`
// event — failed to load, autoplay blocked, no decodable track. Never cuts
// a healthy clip short: a playing clip fires `ended` and advances on its own.
const DEAD_CLIP_SKIP_MS = 1200;

function appHref(id: string) {
  // `/` forwards query params into the Onboarding-workspace redirect.
  return `/?app=${encodeURIComponent(id)}`;
}

export function AppWallSplash({
  longForm = false,
  invite,
  unknownApp,
  initialApp,
}: {
  longForm?: boolean;
  // Present when rendered by the `/for/<slug>` invite route: applies (+persists)
  // the resolved cohort so it sticks on later visits to `/`.
  invite?: { cohort: string | null };
  // The `?app=<id>` the visitor arrived with, when it matched no flagship app.
  // Renders a short "we couldn't find that" line so a mistyped marketing link
  // does not dead-end on an ordinary-looking landing page.
  unknownApp?: string;
  // The `?app=<id>` the visitor arrived with, matched: open that app's dialog
  // on arrival, so a shared `/?app=docs` link lands on the SAME surface a
  // landing card click opens (its "Open app" CTA is sign-up returning to the app).
  initialApp?: string;
} = {}) {
  const t = useT();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dialog, setDialog] = useState<{ app: AppTile; rect: DOMRect } | null>(null);
  const [returning, setReturning] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  // Data-saver / very slow connection: keep it to the one visible clip.
  const [saveData, setSaveData] = useState(false);
  // Turns true once the first clip is actually streaming — then we warm the
  // rest in the background so a card is ready to play the moment it's focused.
  const [warm, setWarm] = useState(false);
  // Cohort (from a `?cohort=` link or mapped UTM, persisted) reorders the deck
  // to lead with that audience's apps. null = the default order.
  const [cohort, setCohort] = useState<string | null>(null);
  const apps = useMemo(() => orderAppsForCohort(cohort), [cohort]);

  // A matched `?app=<id>` deep link: open that app's dialog on arrival. The
  // origin rect is a small centered box (there is no clicked tile to zoom
  // from), so the dialog zooms in from center. Once only, on mount.
  const initialAppOpened = useRef(false);
  useEffect(() => {
    if (!initialApp || initialAppOpened.current) return;
    const target = orderAppsForCohort(null).find((a) => a.id === initialApp);
    if (!target) return;
    initialAppOpened.current = true;
    const size = 96;
    const rect = new DOMRect(
      window.innerWidth / 2 - size / 2,
      window.innerHeight / 2 - size / 2,
      size,
      size,
    );
    setDialog({ app: target, rect });
  }, [initialApp]);
  const analytics = useAnalytics();
  // Experimental switcher (longForm only): 0–9 color scheme, g/b/c layout,
  // x cycles the card gradient style.
  // BAKED baseline (was the live experiment switcher): "S3-0-C-maxglow" + page 0
  // = hero variant 3 · Lime noir (scheme 0) · coverflow · glow · page layout 0.
  const scheme = 0;
  const fx: 'glow' | 'bold' | 'mesh' | 'faint' = 'glow';
  const heroVariant = 3;
  const sectionVariant = 0;

  // Hide the server-rendered crawler landing (app/(splash)/page.tsx renders a
  // semantic SSR block behind this component so crawlers see real content;
  // gap E1). Once this client tree is mounted it owns the screen, so the SSR


  // Returning-visitor detection (mirrors LoginDialogBody + the landing CTA):
  // anyone who has signed in or finished onboarding before sees "Log in"; a
  // first-time visitor sees "Sign up for free". Purely a CTA label now.
  useEffect(() => {
    // An invite (`/for/<slug>`) applies its cohort, persisting it so it sticks
    // on later visits to `/`.
    if (invite) {
      const c = invite.cohort ?? resolveCohort();
      setCohort(c);
      if (invite.cohort) {
        try {
          localStorage.setItem(COHORT_STORAGE_KEY, invite.cohort);
          document.cookie = `${COHORT_STORAGE_KEY}=${invite.cohort}; path=/; max-age=${90 * 86400}; samesite=lax`;
        } catch {
          /* ignore */
        }
      }
      return;
    }
    let isReturning = false;
    try {
      const ls = window.localStorage;
      isReturning = ls.getItem('robutler_has_logged_in') === '1' || ls.getItem('robutler_onboarding_done') === '1';
    } catch {
      /* ignore */
    }
    setReturning(isReturning);
    setCohort(resolveCohort());
  }, [invite]);

  useEffect(() => {
    analytics.trackPageView('/welcome');
  }, [analytics]);

  // Cohort attribution: fire once when a cohort is resolved, tagged by source
  // (an `/for/<slug>` invite vs an organic `?cohort=`/UTM/persisted hit).
  const cohortTracked = useRef(false);
  useEffect(() => {
    if (!cohort || cohortTracked.current) return;
    cohortTracked.current = true;
    analytics.trackCohort(cohort, invite ? 'invite' : 'organic');
  }, [cohort, invite, analytics]);

  // Track prefers-reduced-motion: such viewers get a single looping clip
  // (the spotlight never walks), matching the no-rotation behaviour they had.
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!m) return;
    const update = () => setReduceMotion(m.matches);
    update();
    m.addEventListener?.('change', update);
    return () => m.removeEventListener?.('change', update);
  }, []);

  // Respect Save-Data / 2g-3g: don't background-warm the other clips, and let
  // the spotlit one loop in place instead of walking (so only it loads).
  useEffect(() => {
    const c = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string; addEventListener?: (t: string, f: () => void) => void; removeEventListener?: (t: string, f: () => void) => void } }).connection;
    if (!c) return;
    const update = () => setSaveData(Boolean(c.saveData) || /(^|-)2g$|^3g$/.test(c.effectiveType ?? ''));
    update();
    c.addEventListener?.('change', update);
    return () => c.removeEventListener?.('change', update);
  }, []);

  // Per-card preload: the spotlit clip loads eagerly (first paint priority);
  // the rest stay 'none' until the first is streaming, then warm to 'metadata'
  // (first bytes) so focus-to-play is instant. Save-Data keeps the rest off.
  const preloadFor = useCallback(
    (i: number): 'auto' | 'metadata' | 'none' => {
      if (i === active) return 'auto';
      if (saveData) return 'none';
      return warm ? 'metadata' : 'none';
    },
    [active, saveData, warm],
  );

  // Spotlight walks left→right, one clip playing at a time — but it advances
  // only when the spotlit clip plays through to its end (see AppCard), so each
  // app's recording is seen in full. Hover steals + holds; a dialog holds too.
  const advance = useCallback(() => {
    setActive((i) => (i + 1) % apps.length);
  }, []);

  // Renders one app tile with the shared spotlight/zoom wiring. `fill` lets the
  // card stretch to its container (bento hero tile, coverflow cards).
  const cardFor = (app: AppTile, i: number, fill = false) => (
    <AppCard
      key={app.id}
      app={app}
      fx={fx}
      fill={fill}
      active={i === active && !paused && !dialog}
      spotlit={i === active}
      reduceMotion={reduceMotion}
      saveData={saveData}
      preload={preloadFor(i)}
      onReady={() => setWarm(true)}
      onHover={() => {
        setPaused(true);
        setActive(i);
      }}
      onLeave={() => setPaused(false)}
      onOpen={(rect) => {
        // Coverflow: a tap on a side card rolls it to the front rather than
        // opening it; only the focused (front) card opens the dialog. (On
        // desktop, hover already promotes a side card to front, so a click
        // there lands on the focused branch and opens — matching the grid.)
        if (fill && i !== active) {
          setPaused(true);
          setActive(i);
          return;
        }
        setDialog({ app, rect });
        analytics.trackContentAction(app.id, 'app_preview_open');
      }}
      onAdvance={advance}
    />
  );

  // Carousel interaction (coverflow): wheel (horizontal), drag, swipe. Steps the
  // focused index with wrap-around, and pauses the auto-walk on interaction.
  const CARD_PX = 160; // px of drag ≈ one card
  const TAP_SLOP_PX = 18; // net px of travel under which a press counts as a tap
  const dragStart = useRef<number | null>(null);
  const downX = useRef(0); // raw clientX at pointerdown, for tap-vs-drag
  const draggedRef = useRef(false);
  const wheelEnd = useRef<number | null>(null); // debounce: end-of-swipe → snap
  const lastX = useRef(0);
  const lastT = useRef(0);
  const vel = useRef(0); // px/ms, sampled during drag
  const dragDxRef = useRef(0); // mirror of dragDx for the rAF loop
  const momentumRaf = useRef<number | null>(null);
  const [dragDx, setDragDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [coasting, setCoasting] = useState(false);
  // Inertial release: carry the flick's velocity with friction, then ease into
  // the nearest card. A slow release (≈0 velocity) just snaps to the nearest.
  const startMomentum = useCallback(() => {
    let v = Math.max(-2.5, Math.min(2.5, vel.current)); // clamp wild flicks
    let offset = dragDxRef.current;
    let snapping = false;
    let target = 0;
    setCoasting(true);
    const tick = () => {
      if (!snapping) {
        offset += v * 16;
        v *= 0.93;
        if (Math.abs(v) < 0.04) {
          snapping = true;
          target = Math.round(offset / CARD_PX) * CARD_PX;
        }
      } else {
        offset += (target - offset) * 0.22;
        if (Math.abs(target - offset) < 0.6) {
          const cards = Math.round(target / CARD_PX);
          if (cards !== 0) setActive((a) => (((a - cards) % apps.length) + apps.length) % apps.length);
          dragDxRef.current = 0;
          setDragDx(0);
          setCoasting(false);
          momentumRaf.current = null;
          return;
        }
      }
      dragDxRef.current = offset;
      setDragDx(offset);
      momentumRaf.current = requestAnimationFrame(tick);
    };
    momentumRaf.current = requestAnimationFrame(tick);
  }, []);
  // Continuous finger-follow drag: while pressed, cards track the pointer (window
  // listeners so the gesture survives leaving the carousel); on release, momentum.
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      if (dragStart.current == null) return;
      const dx = e.clientX - dragStart.current;
      const now = performance.now();
      const dt = now - lastT.current;
      if (dt > 0) vel.current = (e.clientX - lastX.current) / dt;
      lastX.current = e.clientX;
      lastT.current = now;
      dragDxRef.current = dx;
      setDragDx(dx);
    };
    const up = (e: PointerEvent) => {
      // Tap vs drag by NET travel from press to release — not "ever moved >Npx".
      // Touch taps jitter a fair bit mid-press but land back near the origin, so
      // judge by net distance with a generous slop (a real swipe travels far
      // more than this). Anything under it is a tap and passes through to the
      // card's click (open the dialog / roll a side card to front). A nudge
      // shorter than the slop just snaps back, so erring large costs nothing.
      draggedRef.current = Math.abs(e.clientX - downX.current) > TAP_SLOP_PX;
      dragStart.current = null;
      setDragging(false);
      startMomentum();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging, startMomentum]);
  useEffect(() => () => {
    if (momentumRaf.current != null) cancelAnimationFrame(momentumRaf.current);
    if (wheelEnd.current) clearTimeout(wheelEnd.current);
  }, []);
  const carouselHandlers = {
    onWheel: (e: ReactWheelEvent) => {
      // Horizontal intent only; let vertical scroll fall through to the page.
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY) || Math.abs(e.deltaX) < 2) return;
      // Roll continuously: feed the swipe into the same offset + momentum model
      // as drag, so a trackpad swipe glides through cards instead of snapping
      // one at a time.
      if (momentumRaf.current != null) {
        cancelAnimationFrame(momentumRaf.current);
        momentumRaf.current = null;
      }
      const now = performance.now();
      const dt = Math.max(8, now - lastT.current);
      lastT.current = now;
      dragDxRef.current -= e.deltaX; // swipe right (deltaX > 0) → next card
      vel.current = -e.deltaX / dt; // px/ms of the offset, for the release glide
      setPaused(true);
      setCoasting(true); // no CSS transition while rolling
      setDragDx(dragDxRef.current);
      // When the swipe (and any OS momentum tail) stops, glide to rest + snap.
      if (wheelEnd.current) clearTimeout(wheelEnd.current);
      wheelEnd.current = window.setTimeout(() => startMomentum(), 80);
    },
    onDragStart: (e: ReactDragEvent) => e.preventDefault(),
    onPointerDown: (e: ReactPointerEvent) => {
      // Grabbing mid-coast cancels momentum and continues from the current offset.
      if (momentumRaf.current != null) {
        cancelAnimationFrame(momentumRaf.current);
        momentumRaf.current = null;
        setCoasting(false);
      }
      dragStart.current = e.clientX - dragDxRef.current;
      downX.current = e.clientX; // tap origin; net travel at release decides tap vs drag
      draggedRef.current = false;
      lastX.current = e.clientX;
      lastT.current = performance.now();
      vel.current = 0;
      setPaused(true);
      setDragging(true);
    },
    onClickCapture: (e: ReactMouseEvent) => {
      if (draggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        draggedRef.current = false;
      }
    },
  };

  return (
    <div
      // The scheme vars below land on THIS element, so `body` keeps the app's
      // light `--color-background` and rubber-band overscroll flashes white
      // above the hero. The marker lets globals.css paint the document itself
      // to match — see the `[data-splash-dark]` rule there.
      data-splash-dark={longForm && SCHEMES[scheme].dark ? '' : undefined}
      className={`relative flex min-h-screen w-full flex-col bg-background ${longForm ? '' : 'sm:h-screen sm:overflow-hidden'} ${longForm && SCHEMES[scheme].dark ? 'dark' : ''}`}
      style={longForm ? SCHEMES[scheme].vars : undefined}
    >
      <LiquidGlassDefs />
      {/* EXACT live-landing aura, viewport-anchored behind the content. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <GuestLandingGradient force />
      </div>
      {/* ── Top bar ── (dropped for full-takeover hero variants) ── */}
      {!(longForm && HERO_HIDE_NAV.has(heroVariant)) && (
      <header className={`relative z-10 flex items-center justify-between px-5 py-4 sm:px-8 ${longForm ? 'sticky top-0 backdrop-blur-md' : ''}`}>
        <BrandMark />
        {/* Mirrors the hero CTA off returning-visitor detection: a known
            returning visitor gets "Log in"; a first-time / incognito visitor
            gets "Sign up". Keeps both corner + hero pointing the same way. */}
        <Link
          href={returning ? '/login' : '/login?signup=1'}
          aria-label={returning ? t('home.cta.logIn') : t('splash.signUpFree')}
          onClick={() => analytics.trackNavClick(returning ? 'login' : 'signup', 'welcome_header')}
          className="liquid-glass inline-flex h-10 items-center rounded-2xl px-4 text-sm font-medium text-foreground transition-colors hover:bg-accent/40"
        >
          {returning ? t('home.cta.logIn') : t('home.cta.signUp')}
        </Link>
      </header>
      )}

      {/* A `/?app=<id>` link whose id resolved to nothing. Without this the
          visitor silently lands on the plain landing page and never learns the
          link was wrong — indistinguishable from arriving at `/` on purpose. */}
      {unknownApp && (
        <p
          role="status"
          // The smoke anon-funnel's NEGATIVE marker. A flagship app that is in
          // FLAGSHIP_APPS but not seeded into this environment lands here, and
          // without something to match on, the funnel can only wait out its
          // whole 90s tile budget for an Open-app CTA that will never render
          // (dev v0.0.332, 2026-09-21: `revent` cost the gate 90s and gave no
          // hint of why). Racing this against the CTA turns that into a
          // one-second, self-describing failure.
          data-testid="splash-unknown-app"
          className="relative z-10 px-5 pt-2 text-center text-sm text-muted-foreground"
        >
          {t('splash.unknownAppPre')} <span className="font-medium text-foreground">{unknownApp}</span>{t('splash.unknownAppPost')}
        </p>
      )}

      {/* ── Body ──────────────────────────────────────────────── */}
      <main className={`relative z-10 flex w-full flex-1 flex-col items-center px-5 pt-6 pb-6 md:pb-0 ${longForm ? 'lg:min-h-[86vh]' : ''}`}>
        {/* Pitch cluster — hero, promise pills and the app wall as one centred
            unit so they read as a group instead of scattering down the page. */}
        <div className="flex w-full flex-1 flex-col items-center justify-center gap-8 sm:gap-10">
          {/* Hero. Production = original centered block; long-form = the variant
              experiment, switchable with shift+0..9. */}
          {!longForm && (
            <div className="flex flex-col items-center text-center">
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl 2xl:text-7xl">
                {t('splash.youtubeOfSoftware')}
              </h1>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground/70 sm:text-lg">
                {t('splash.tagline')}
              </p>
              <div className="mt-7 flex flex-col items-center gap-2.5">
                <Link href={returning ? '/login' : '/login?signup=1'} className="btn-primary gap-2 rounded-full px-5">
                  {returning ? t('home.cta.logIn') : t('splash.signUpFree')}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                {PROMISES.map((p) => (
                  <span key={p} className="liquid-glass inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-foreground">
                    <Check className="h-3.5 w-3.5 text-primary" />
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}
          {longForm && <HeroVariant v={heroVariant} returning={returning} />}

          {/* App wall. Short-form = the original static grid; long-form (the
              anon `/` landing) = the coverflow carousel below. */}
          {!longForm && (
          <div className="grid w-full max-w-5xl grid-cols-2 gap-3 sm:grid-cols-4">
            {apps.map((app, i) => (
            <AppCard
              key={app.id}
              app={app}
              active={i === active && !paused && !dialog}
              spotlit={i === active}
              reduceMotion={reduceMotion}
              onHover={() => {
                setPaused(true);
                setActive(i);
              }}
              onLeave={() => setPaused(false)}
              onOpen={(rect) => { setDialog({ app, rect }); analytics.trackContentAction(app.id, 'app_preview_open'); }}
              onAdvance={advance}
            />
          ))}
          </div>
          )}

          {longForm && (
            <div
              className="relative w-full max-w-5xl select-none"
              style={{ height: 'min(64vw, 300px)', perspective: '1500px', transformStyle: 'preserve-3d', touchAction: 'pan-y', cursor: 'grab' }}
              {...carouselHandlers}
            >
              {apps.map((app, i) => {
                const n = apps.length;
                // Full modulo wrap so the ring stays continuous no matter how far
                // a fast flick's momentum carries dragDx (no gap / blank window).
                let off = (((i - active + dragDx / CARD_PX) % n) + n) % n;
                if (off > n / 2) off -= n;
                const ao = Math.abs(off);
                // Keep every card mounted (far ones just fade to opacity 0) — never
                // unmount during a coast, or the <video> would remount and flash.
                return (
                  <div
                    key={app.id}
                    className="absolute left-1/2 top-1/2"
                    style={{
                      width: 'min(82vw, 380px)',
                      height: 'min(54vw, 250px)',
                      transform: `translate(-50%, -50%) translateX(${off * 42}%) translateZ(${-ao * 110}px) rotateY(${-off * 34}deg) scale(${Math.max(0.55, 1 - ao * 0.1)})`,
                      zIndex: 30 - Math.round(ao),
                      opacity: ao > 2.6 ? 0 : 1 - ao * 0.2,
                      transition: dragging || coasting ? 'none' : 'transform .5s cubic-bezier(.4,0,.2,1), opacity .5s',
                      pointerEvents: ao > 1.6 ? 'none' : 'auto',
                    }}
                  >
                    {cardFor(app, i, true)}
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Credibility logos pinned near the foot of the hero (long-form): no
            label, slightly larger, soft. The flex-1 pitch cluster above pushes
            these to the bottom of the hero viewport. */}
        {longForm && !HERO_HIDE_PARTNERS.has(heroVariant) && (
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 pt-8 opacity-70">
            <a href="https://cloud.google.com/startup" target="_blank" rel="noopener noreferrer" aria-label="Google for Startups" className="transition-opacity hover:opacity-100">
              <Image src="/images/partners/google-for-startups.svg" alt="Google for Startups" width={170} height={21} className="h-5 w-auto sm:h-6" />
            </a>
            <a href="https://www.nvidia.com/en-us/startups/" target="_blank" rel="noopener noreferrer" aria-label="NVIDIA Inception Program" className="transition-opacity hover:opacity-100">
              <Image src="/images/partners/nvidia-inception.svg" alt="NVIDIA Inception" width={150} height={50} className="h-10 w-auto sm:h-11" />
            </a>
            <a href="https://stripe.com/startups" target="_blank" rel="noopener noreferrer" aria-label="Stripe Startups" className="transition-opacity hover:opacity-100">
              <Image src="/images/partners/stripe-startups.svg" alt="Stripe Startups" width={180} height={20} className="h-5 w-auto sm:h-6" />
            </a>
          </div>
        )}

        {/* Partner badges (non-longForm). */}
        {!longForm && (
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-5 pt-8">
          <a href="https://cloud.google.com/startup" target="_blank" rel="noopener noreferrer" aria-label="Google for Startups" className="opacity-80 transition-opacity hover:opacity-100">
            <Image src="/images/partners/google-for-startups.svg" alt="Google for Startups" width={170} height={21} className="h-5 w-auto sm:h-6" />
          </a>
          <a href="https://www.nvidia.com/en-us/startups/" target="_blank" rel="noopener noreferrer" aria-label="NVIDIA Inception Program" className="opacity-80 transition-opacity hover:opacity-100">
            <Image src="/images/partners/nvidia-inception.svg" alt="NVIDIA Inception" width={150} height={50} className="h-10 w-auto sm:h-11" />
          </a>
          <a href="https://stripe.com/startups" target="_blank" rel="noopener noreferrer" aria-label="Stripe Startups" className="opacity-80 transition-opacity hover:opacity-100">
            <Image src="/images/partners/stripe-startups.svg" alt="Stripe Startups" width={180} height={20} className="h-5 w-auto sm:h-5" />
          </a>
        </div>
        )}
      </main>

      {/* Long-form scrollytelling — only on the `/welcome` preview route. */}
      {longForm && <WelcomeSections returning={returning} variant={sectionVariant} />}

      {/* ── Footer ────────────────────────────────────────────── */}
      {/* Short-form foot; long-form has its own fuller footer in WelcomeSections. */}
      {!longForm && (
      <footer className="relative z-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-5 pb-8 pt-4 text-xs text-muted-foreground/70 sm:justify-between sm:px-8 sm:pb-4">
        <span>© Robutler</span>
        <nav className="flex items-center gap-4">
          {/* The only crawlable link from this page into the catalogue. Every
              tile above points at `/?app=<id>`, which resolves to an ephemeral
              `/w/<uuid>` viewer that is deliberately noindex — so without this
              anchor the 156 app and agent pages have no inbound link from any
              indexable page at all, and a search engine can only reach them via
              the sitemap. Same tab on purpose: this is site navigation, not an
              outbound reference like the three beside it. */}
          <a href="/discover" className="transition-colors hover:text-foreground">Browse apps</a>
          <a href="/docs" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">Docs</a>
          <a href="/doc/terms-of-service" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">Terms</a>
          <a href="/doc/privacy-policy" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">Privacy</a>
        </nav>
      </footer>
      )}

      {dialog && (
        <ZoomDialog app={dialog.app} originRect={dialog.rect} returning={returning} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

// Hero variants that drop chrome for a full-screen takeover (partners strip / nav).
const HERO_HIDE_PARTNERS = new Set([1, 2, 4, 5, 7]);
const HERO_HIDE_NAV = new Set([1]);

/**
 * Hero layout experiment (long-form /welcome only). 10 bold arrangements of the
 * same content (headline "Use. Remix. Earn." + sub + CTA), switched with
 * shift+0..9. Temporary scaffolding — once one wins it gets baked in + the rest
 * deleted.
 */
function HeroVariant({ v, returning }: { v: number; returning: boolean }) {
  const t = useT();
  const analytics = useAnalytics();
  const authHref = returning ? '/login' : '/login?signup=1';
  const authLabel = returning ? t('home.cta.logIn') : t('splash.signUpFree');
  const sub = "Use for free the agentic apps you'd pay monthly for.";
  const cta = (
    <Link
      href={authHref}
      onClick={() => analytics.trackNavClick(returning ? 'login' : 'signup', 'welcome_hero')}
      className="btn-primary gap-2 rounded-full px-5"
    >
      {authLabel}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
  const helper = null;

  // 1 — giant full-bleed type (drops nav + partners)
  if (v === 1)
    return (
      <div className="flex w-full max-w-6xl flex-col items-center text-center">
        <h1 className="font-display font-bold tracking-tighter text-foreground" style={{ fontSize: 'clamp(3rem, 14vw, 13rem)', lineHeight: 0.84 }}>
          {t('splash.useRemixEarn')}
        </h1>
        <div className="mt-6 flex flex-col items-center gap-2.5">
          {cta}
          <span className="text-sm text-muted-foreground">{sub}</span>
        </div>
      </div>
    );
  // 2 — outline type, accent word
  if (v === 2)
    return (
      <div className="flex w-full max-w-5xl flex-col items-center text-center">
        <h1 className="font-display font-bold tracking-tight" style={{ fontSize: 'clamp(2.5rem, 11vw, 9.5rem)', lineHeight: 0.88 }}>
          <span style={{ WebkitTextStroke: '2px var(--color-foreground)', color: 'transparent' }}>Use. Remix. </span>
          <span style={{ color: 'var(--color-primary)' }}>Earn.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base text-foreground/70 sm:text-lg">{sub}</p>
        <div className="mt-7 flex flex-col items-center gap-2.5">{cta}{helper}</div>
      </div>
    );
  // 3 — category-led, bold
  if (v === 3)
    return (
      <div className="flex flex-col items-center text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.4em] text-primary">{t('splash.proGradeApps')}</p>
        <h1 className="mt-4 font-display font-bold tracking-tight text-foreground" style={{ fontSize: 'clamp(2.5rem, 9vw, 8rem)', lineHeight: 0.9 }}>{t('splash.useRemixEarn')}</h1>
        <p className="mt-5 max-w-xl text-base text-foreground/70 sm:text-lg">{sub}</p>
        <div className="mt-7 flex flex-col items-center gap-2.5">{cta}{helper}</div>
      </div>
    );
  // 4 — left editorial, oversized (drops partners)
  if (v === 4)
    return (
      <div className="flex w-full max-w-6xl flex-col items-start text-left">
        <h1 className="font-display font-bold tracking-tighter text-foreground" style={{ fontSize: 'clamp(2.75rem, 12vw, 12rem)', lineHeight: 0.85 }}>{t('splash.useRemixEarn')}</h1>
        <p className="mt-5 max-w-md text-base text-foreground/70 sm:text-lg">{sub}</p>
        <div className="mt-7 flex flex-wrap items-center gap-4">{cta}{helper}</div>
      </div>
    );
  // 5 — stacked giant words, kinetic alignment + accent (drops partners)
  if (v === 5)
    return (
      <div className="flex w-full max-w-6xl flex-col text-left">
        <h1 className="font-display font-bold tracking-tighter text-foreground" style={{ fontSize: 'clamp(3rem, 15vw, 15rem)', lineHeight: 0.82 }}>
          <span className="block">Use.</span>
          <span className="block text-right" style={{ color: 'var(--color-primary)' }}>Remix.</span>
          <span className="block">Earn.</span>
        </h1>
        <p className="mt-6 max-w-md text-base text-foreground/70 sm:text-lg">{sub}</p>
        <div className="mt-6 flex flex-wrap items-center gap-4">{cta}{helper}</div>
      </div>
    );
  // 6 — kinetic gradient words
  if (v === 6)
    return (
      <div className="flex w-full max-w-5xl flex-col items-center text-center">
        <h1 className="font-display font-bold tracking-tight" style={{ fontSize: 'clamp(2.5rem, 11vw, 9.5rem)', lineHeight: 0.88 }}>
          <span style={{ backgroundImage: 'linear-gradient(120deg,#7c6cf0,#ec4899)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Use. </span>
          <span style={{ backgroundImage: 'linear-gradient(120deg,#38bdf8,#34d399)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Remix. </span>
          <span style={{ backgroundImage: 'linear-gradient(120deg,#fbbf24,#fb923c)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Earn.</span>
        </h1>
        <p className="mt-5 max-w-xl text-base text-foreground/70 sm:text-lg">{sub}</p>
        <div className="mt-7 flex flex-col items-center gap-2.5">{cta}{helper}</div>
      </div>
    );
  // 7 — eyebrow pill (drops partners)
  if (v === 7)
    return (
      <div className="flex flex-col items-center text-center">
        <span className="rounded-full border border-border bg-card/60 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground backdrop-blur">
          {t('splash.youtubeOfSoftware')}
        </span>
        <h1 className="mt-6 font-display font-bold tracking-tight text-foreground" style={{ fontSize: 'clamp(2.5rem, 9vw, 8rem)', lineHeight: 0.9 }}>{t('splash.useRemixEarn')}</h1>
        <p className="mt-5 max-w-xl text-base text-foreground/70 sm:text-lg">{sub}</p>
        <div className="mt-7 flex flex-col items-center gap-2.5">{cta}{helper}</div>
      </div>
    );
  // 8 — split-screen cinematic
  if (v === 8)
    return (
      <div className="grid w-full max-w-6xl grid-cols-1 overflow-hidden rounded-3xl border border-border/40 lg:grid-cols-2">
        <div className="flex items-center p-8 text-left lg:p-12">
          <h1 className="font-display font-bold tracking-tighter text-foreground" style={{ fontSize: 'clamp(2.5rem, 8vw, 6.5rem)', lineHeight: 0.86 }}>{t('splash.useRemixEarn')}</h1>
        </div>
        <div
          className="flex flex-col justify-center gap-5 p-8 text-left lg:p-12"
          style={{ background: 'linear-gradient(140deg, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent)' }}
        >
          <p className="text-lg text-foreground/80 sm:text-xl">{sub}</p>
          <div className="flex flex-wrap items-center gap-4">{cta}{helper}</div>
        </div>
      </div>
    );
  // 9 — minimal / quiet
  if (v === 9)
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <h1 className="font-display text-3xl font-medium tracking-tight text-foreground sm:text-4xl">{t('splash.useRemixEarn')}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{sub}</p>
        <div className="mt-8">{cta}</div>
      </div>
    );
  // 0 — centered (default)
  return (
    <div className="flex flex-col items-center text-center">
      <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl 2xl:text-7xl">{t('splash.useRemixEarn')}</h1>
      <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground/70 sm:text-lg">{sub}</p>
      <div className="mt-7 flex flex-col items-center gap-2.5">{cta}{helper}</div>
    </div>
  );
}

/** Brand mark — workspace glass-pill logo, no dropdown/menu on the splash. */
function BrandMark() {
  return (
    <div className="liquid-glass inline-flex h-10 select-none items-center gap-2 rounded-2xl px-3.5" aria-label="Robutler">
      <Image src="/icons/Logo_B.svg" alt="Robutler" width={22} height={22} className="block dark:hidden" />
      <Image src="/icons/Logo_W.svg" alt="Robutler" width={22} height={22} className="hidden dark:block" />
      <Image src="/icons/Wm_B.svg" alt="Robutler" width={92} height={13} className="hidden dark:hidden sm:block" />
      <Image src="/icons/Wm_W.svg" alt="Robutler" width={92} height={13} className="hidden dark:sm:block" />
    </div>
  );
}

/**
 * Faint ▶ in a card's bottom title stripe — a quiet marker that each tile is a
 * live clip (on-brand with "The YouTube of Software"). Passive, not a button:
 * clicking the card opens the app. Kept off the thumbnail so the preview stays
 * chrome-free, and always-on so it reads the same whatever the clip is doing.
 */
function PlayBadge() {
  return <Play aria-hidden className="h-3.5 w-3.5 shrink-0 fill-current text-muted-foreground/30" />;
}

/**
 * Modern, minimal app tile — a video-thumbnail card (media on top,
 * title below). Only the spotlit card's clip plays; the icon poster
 * cross-fades to the video once it's actually playing. Click FLIP-zooms
 * the card to a centred dialog (handled by the parent via `onOpen`).
 * No size-changing affordances on the grid itself: border-color,
 * box-shadow, opacity and transform are shift-free.
 */
function AppCard({
  app,
  active,
  spotlit,
  reduceMotion,
  saveData = false,
  preload = 'metadata',
  onReady,
  onHover,
  onLeave,
  onOpen,
  onAdvance,
  fx = 'faint',
  fill = false,
}: {
  app: AppTile;
  active: boolean;
  spotlit: boolean;
  reduceMotion: boolean;
  saveData?: boolean;
  preload?: 'auto' | 'metadata' | 'none';
  onReady?: () => void;
  fx?: 'glow' | 'bold' | 'mesh' | 'faint';
  fill?: boolean;
  onHover: () => void;
  onLeave: () => void;
  onOpen: (rect: DOMRect) => void;
  onAdvance: () => void;
}) {
  const t = useT();
  const Icon = app.icon;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const failedRef = useRef(false);
  const sampledRef = useRef(false);
  // Once the clip's first frame is decoded we show it as the poster (instead of
  // the icon) and sample its average colour for the card's glow.
  const [ready, setReady] = useState(false);
  const [glow, setGlow] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);
  // 0→1 playback position of the spotlit clip, drives the progress bar.
  const [progress, setProgress] = useState(0);

  // Sample the frame's average colour for the glow. Guarded so it runs once and
  // ignores an all-black fade-in frame (keeps the curated tint and retries on a
  // later frame instead).
  const sampleGlow = useCallback(() => {
    const v = videoRef.current;
    if (!v || sampledRef.current || !v.videoWidth) return;
    try {
      const c = document.createElement('canvas');
      c.width = 1;
      c.height = 1;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(v, 0, 0, 1, 1); // whole frame → 1px = average colour
      const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
      if (r + g + b < 24) return; // still black (intro fade) — try a later frame
      sampledRef.current = true;
      setGlow(`#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`);
    } catch {
      sampledRef.current = true; // cross-origin / unsupported — keep the curated tint
    }
  }, []);

  // Show the clip's first frame as the poster, then sample its colour on a frame
  // that's actually been presented (rVFC) — drawing right at `loadeddata` returns
  // a black buffer in some browsers.
  const onFrameReady = useCallback(() => {
    setReady(true);
    const v = videoRef.current as
      | (HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number })
      | null;
    if (!v) return;
    if (typeof v.requestVideoFrameCallback === 'function') v.requestVideoFrameCallback(() => sampleGlow());
    else window.setTimeout(sampleGlow, 140);
  }, [sampleGlow]);

  // Restart + play this card's clip from the top whenever it takes the
  // spotlight; pause + reset when it loses it. Don't re-request a clip that
  // failed to load — leave the icon poster up.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (spotlit && !failedRef.current) {
      try { v.currentTime = 0; } catch { /* not seekable yet */ }
      setProgress(0);
      const p = v.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else if (!spotlit) {
      v.pause?.();
      try { v.currentTime = 0; } catch { /* not seekable yet */ }
      setProgress(0);
    }
  }, [spotlit]);

  // Background warm: once the parent flips this idle card from 'none' to
  // 'metadata', pull the first bytes now so focus-to-play is instant. (Some
  // browsers ignore a dynamic preload change without an explicit load.)
  useEffect(() => {
    const v = videoRef.current;
    if (!v || spotlit || failedRef.current) return;
    // Only warm a cold element; never re-load one that already has data (that
    // reset is what flashes the poster back in during a scroll).
    if (preload !== 'none' && v.readyState === 0) { try { v.load(); } catch { /* ignore */ } }
  }, [preload, spotlit]);

  // Dead-clip / no-clip safety: a healthy clip advances the spotlight via its
  // `ended` event. A card with NO clip (a coming-soon tile) or a failed clip
  // never fires `ended`, so it would strand the carousel — advance on a timer
  // instead (a longer "read" beat for a deliberate no-clip slide, a short one
  // for a clip that errored).
  useEffect(() => {
    if (!active) return;
    if (app.video && !failedRef.current) return;
    const delay = app.video ? DEAD_CLIP_SKIP_MS : 2800;
    const t = window.setTimeout(onAdvance, delay);
    return () => window.clearTimeout(t);
  }, [active, onAdvance, app.video]);

  // Glow tint: the first frame's average colour once sampled, else the curated
  // per-app tint (both 6-digit hex, so the alpha-suffix below stays valid).
  const glowColor = glow ?? app.tint;

  return (
    <Link
      href={appHref(app.id)}
      onClick={(e) => {
        e.preventDefault();
        onOpen(e.currentTarget.getBoundingClientRect());
      }}
      onMouseEnter={() => {
        setHovered(true);
        onHover();
      }}
      onMouseLeave={() => {
        setHovered(false);
        onLeave();
      }}
      onFocus={onHover}
      onBlur={onLeave}
      draggable={false}
      className={`group relative block ${fill ? 'h-full' : ''}`}
    >
      {/* The hover-lift lives on this inner layer, NOT the <Link>. The Link
          (the hover/hit target) stays put, so the card lifting up can't slide
          its own bottom edge out from under the cursor — which would otherwise
          drop hover, settle the card back down, and jitter in a loop. */}
      <div
        className={`relative overflow-hidden rounded-xl border bg-card/70 backdrop-blur-sm transition-[box-shadow,border-color] duration-200 group-hover:shadow-xl ${
          fill ? 'flex h-full flex-col ' : ''
        }${spotlit ? 'border-foreground/25 shadow-lg' : 'border-border/60'}`}
        style={
          // The tint is carried by the soft glow (boxShadow) only. We do NOT
          // tint the border: a `glowColor`-colored 1px border renders as a hard
          // bright stripe down the card's left/right edges under the coverflow's
          // 3D transform. The neutral className border (border-border/60) stays.
          fx === 'glow'
            ? {
                boxShadow: hovered
                  ? `0 0 30px 0 ${glowColor}, 0 0 100px -8px ${glowColor}`
                  : `0 0 24px -2px ${glowColor}, 0 0 72px -16px ${glowColor}`,
              }
            : undefined
        }
      >
        <div
          className={`splash-card-media relative overflow-hidden ${fill ? 'min-h-0 flex-1' : ''}`}
          style={{
            height: fill ? undefined : 'clamp(64px, 12vh, 132px)',
            // Per-app wash. `fx` controls intensity: faint (subtle, original),
            // bold/glow (vivid duotone), mesh (radial blobs). Video plays over it.
            background: app.join
              ? `linear-gradient(140deg, ${app.tint}, ${app.tint2 ?? app.tint})`
              : fx === 'faint'
                ? `linear-gradient(140deg, ${app.tint}29, ${app.tint}0a)`
                : fx === 'mesh'
                  ? `radial-gradient(circle at 25% 20%, ${app.tint}, transparent 60%), radial-gradient(circle at 85% 90%, ${app.tint2 ?? app.tint}, transparent 55%), #0d0d12`
                  : `linear-gradient(140deg, ${app.tint}, ${app.tint2 ?? app.tint})`,
          }}
        >
          {app.video && (
            <video
              ref={videoRef}
              src={app.video}
              muted
              // Reduced-motion viewers get a single looping clip (no walk); for
              // everyone else the clip plays once and `ended` walks the spotlight.
              loop={reduceMotion || saveData}
              playsInline
              preload={preload}
              onLoadedMetadata={() => {
                // `metadata` preload often stops at HAVE_METADATA without decoding
                // a frame, so an idle card would show no poster. Nudge a tiny seek
                // to force the first frame to decode (→ loadeddata → poster shows).
                const v = videoRef.current;
                if (v && !spotlit && v.readyState < 2) {
                  try { v.currentTime = 0.05; } catch { /* not seekable yet */ }
                }
              }}
              onLoadedData={onFrameReady}
              onPlaying={() => onReady?.()}
              onTimeUpdate={() => {
                const v = videoRef.current;
                if (v && v.duration > 0 && Number.isFinite(v.duration)) {
                  setProgress(Math.min(1, v.currentTime / v.duration));
                }
                if (!sampledRef.current) sampleGlow();
              }}
              onEnded={() => {
                const v = videoRef.current;
                if (active) {
                  // Spotlit clip finished → walk to the next card.
                  onAdvance();
                } else if (spotlit && v) {
                  // Held by hover / dialog: keep the motion going, in place.
                  try { v.currentTime = 0; } catch { /* ignore */ }
                  const p = v.play?.();
                  if (p && typeof p.catch === 'function') p.catch(() => {});
                }
              }}
              onError={() => {
                failedRef.current = true;
                // A dead spotlit clip would otherwise strand the carousel.
                if (active) onAdvance();
              }}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${ready ? 'opacity-100' : 'opacity-0'}`}
            />
          )}
          {/* Icon poster — bridges the brief load before the clip's first frame
              is ready, then fades out. The lead scope card posters the Robutler
              logomark instead of a lucide glyph. */}
          <span className={`absolute inset-0 grid place-items-center transition-opacity duration-300 ${ready ? 'opacity-0' : 'opacity-100'}`}>
            {app.join ? (
              <Image src="/icons/Logo_W.svg" alt="Robutler" width={40} height={40} className="h-9 w-9 transition-transform duration-300 group-hover:scale-110" />
            ) : (
              <Icon className="h-7 w-7 transition-transform duration-300 group-hover:scale-110" style={{ color: fx === 'faint' ? app.tint : '#ffffff' }} />
            )}
          </span>
          {app.soon && (
            <span className="absolute left-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/90 backdrop-blur-sm">
              {t('settings.comingSoon')}
            </span>
          )}
          {/* Spotlight bar pinned to the media's bottom edge (absolute → no
              reflow). Tracks the clip's real playback so it fills as the video
              does, whatever its length. */}
          <div className="absolute inset-x-0 bottom-0 z-[1] h-[3px] bg-foreground/5">
            {active && (
              <div
                className="h-full origin-left bg-primary transition-transform duration-300 ease-linear"
                style={{ transform: `scaleX(${progress})` }}
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium leading-tight text-foreground">{app.name}</p>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{app.sub}</p>
          </div>
          {!app.soon &&
            (spotlit ? (
              // Center card gets an explicit, low-commitment CTA; side cards keep
              // the quiet ▶ (live-clip cue). The pill is a visual affordance only
              // — the whole card is the link, so a tap anywhere opens it.
              <span className="liquid-glass inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-[11px] font-medium text-foreground transition-colors duration-200 group-hover:bg-foreground/10">
                Try
                <ArrowRight className="h-3 w-3" />
              </span>
            ) : (
              <PlayBadge />
            ))}
        </div>
      </div>
    </Link>
  );
}

/**
 * Card → dialog FLIP zoom. The panel is rendered at its final centred
 * position, then on mount we invert it back onto the clicked card's
 * rect and transition to identity — so it appears to grow smoothly out
 * of the card. Closing reverses the transform back to the card.
 */
function ZoomDialog({
  app,
  originRect,
  returning,
  onClose,
}: {
  app: AppTile;
  originRect: DOMRect;
  returning: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const analytics = useAnalytics();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [backdropOn, setBackdropOn] = useState(false);
  /* "OPEN APP" GOES THROUGH SIGN-UP (2026-10-12, the owner: "should we remove
     anon users from landing? the problem is that we cannot reach those users
     with updates and new exciting stuff - lets route everyone via signup").
     It minted an ephemeral guest account behind a bot check and opened the
     app in a guest workspace; a guest has no email address, so nobody who
     came in this way could ever be told about anything again. Now it is the
     sign-up (sign-in for a returning visitor) with the app as the return
     address: the home page opens the app in the new account's own workspace
     (`/?app=<id>`, app/(splash)/authed/home/page.tsx). The screen is
     Robutler's own /login unless the app opts in to a sign-in screen of its
     own (components/auth/login-dialog-body.tsx, 2026-10-13). The button
     keeps its test id: the
     deploy smoke funnel waits for it on every flagship tile. */
  const openHref = `${returning ? '/login?' : '/login?signup=1&'}returnTo=${encodeURIComponent(`/?app=${app.id}`)}`;

  // Transform that maps the panel's current (final) box back onto `rect`.
  const invertTo = (el: HTMLElement, rect: DOMRect) => {
    const t = el.getBoundingClientRect();
    const sx = rect.width / t.width;
    const sy = rect.height / t.height;
    const tx = rect.left + rect.width / 2 - (t.left + t.width / 2);
    const ty = rect.top + rect.height / 2 - (t.top + t.height / 2);
    return `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
  };

  // Grow from the clicked card to the centred panel via the Web Animations
  // API. WAAPI interpolates the keyframes imperatively, so it doesn't depend
  // on reflow/paint-timing or survive-a-frame tricks (which, with React
  // StrictMode's double mount, were collapsing the CSS-transition flip into
  // an instant jump = the "abrupt forth"). Ease-in-out curve (control1.y = 0)
  // starts gently rather than popping out at max velocity.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    el.style.transformOrigin = 'center center';
    const anim = el.animate(
      [{ transform: invertTo(el, originRect) }, { transform: 'translate(0px, 0px) scale(1, 1)' }],
      { duration: 380, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
    );
    setBackdropOn(true);
    return () => anim.cancel();
  }, [originRect]);

  const close = useCallback(() => {
    setBackdropOn(false);
    const el = panelRef.current;
    if (!el) {
      onClose();
      return;
    }
    const anim = el.animate(
      [{ transform: 'translate(0px, 0px) scale(1, 1)' }, { transform: invertTo(el, originRect) }],
      { duration: 240, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' },
    );
    anim.onfinish = () => onClose();
  }, [onClose, originRect]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: backdropOn ? 1 : 0 }}
        onClick={close}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={app.name}
        className="relative z-[61] w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl will-change-transform"
      >
        <div className="relative aspect-video bg-muted">
          {app.video && (
            <video src={app.video} autoPlay muted loop playsInline className="h-full w-full object-cover" />
          )}
          <button
            type="button"
            onClick={close}
            aria-label={t('workspace.canvas.node.close')}
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-background/80 text-foreground backdrop-blur transition-colors hover:bg-background"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-display text-xl font-semibold tracking-tight text-foreground">{app.name}</h3>
              <p className="truncate text-sm text-muted-foreground">{app.sub}</p>
              {app.soon && (
                <p className="mt-1 text-xs font-medium text-foreground/80">Coming soon · sign up to get notified</p>
              )}
            </div>
            {app.join || app.soon ? (
              // Not a runnable app (the scope tile, or a coming-soon app) — the
              // CTA goes to auth (mirrors the hero), with no app to return to.
              <Link
                href={returning ? '/login' : '/login?signup=1'}
                onClick={() => analytics.trackNavClick(returning ? 'login' : 'signup', 'app_dialog')}
                className="btn-primary shrink-0 gap-2 px-5"
              >
                {app.soon ? 'Get notified' : returning ? t('home.cta.logIn') : t('splash.signUpFree')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <Link
                href={openHref}
                onClick={() => {
                  analytics.trackContentAction(app.id, 'app_open');
                  analytics.trackNavClick(returning ? 'login' : 'signup', 'app_dialog');
                }}
                // The smoke anon-funnel's readiness marker: a `/?app=<id>`
                // deep link must land on this actionable CTA.
                data-testid="splash-open-app"
                className="btn-primary shrink-0 gap-2 px-5"
              >
                <span>{t('settings.openApp')}</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
